import IndicadorDashboardItem from "model/indicadores/IndicadorDashboardItem";
import { useEffect, useState } from "react";
import IndicadorService from "service/indicadores/IndicadorService";
import { VictoryAxis, VictoryBar, VictoryChart, VictoryLine, VictoryScatter, VictoryTheme, VictoryTooltip, VictoryVoronoiContainer } from "victory";
import { formatarDataColeta, isMetaAtingida } from "../IndicadorUtil";
import { Dropdown } from "primereact/dropdown";

type Props = {
    dados: IndicadorDashboardItem[];
    unidadeMedida?: string | null;
}

const indicadorService = new IndicadorService();
export default function IndicadorChartBar(props: Props) {

    const [ticks, setTicks] = useState<number[]>([]);
    const [tipoAgregacao, setTipoAgregacao] = useState("total");

    useEffect(() => {
        const maiorColeta = Math.max(...props.dados.map((dado: IndicadorDashboardItem) => (dado?.coleta?.valorColeta || 0)).filter((valor): valor is number => valor !== null));
        const maiorMeta = Math.max(...props.dados.map((dado: IndicadorDashboardItem) => (dado.meta?.meta || 0)).filter((valor): valor is number => valor !== null));
        const maior = Math.max(maiorColeta, maiorMeta);
        const step = Math.ceil((maior) / 4);

        for (let v = 0; v <= maior; v += step) {
            ticks.push(v);
        }

        if (!ticks.includes(maior)) ticks.push(maior);

        setTicks([...ticks]);
    }, [])

    function getColor(options: any) {
        if (!props.dados[options.datum._x - 1].meta?.meta) {
            return "#0E9CFF";
        }

        const meta = props.dados[options.datum._x - 1].meta;

        if (isMetaAtingida(options.datum._y, meta?.meta || 0, meta?.tipo || "")) {
            return "#34A835";
        }
        return "#dc3545";
    }

    function calcularValorAgregado() {
        if (props.dados.length === 0) return 0;
        if (tipoAgregacao === "total") {
            return props.dados.reduce((acc, item) => acc + (item?.coleta?.valorColeta || 0), 0);
        }
        if (tipoAgregacao === "media") {
            return props.dados.reduce((acc, item) => acc + (item?.coleta?.valorColeta || 0), 0) / props.dados.length;
        }
        if (tipoAgregacao === "maximo") {
            return Math.max(...props.dados.map((dado: IndicadorDashboardItem) => (dado?.coleta?.valorColeta || 0)).filter((valor): valor is number => valor !== null));
        }
        if (tipoAgregacao === "minimo") {
            return Math.min(...props.dados.map((dado: IndicadorDashboardItem) => (dado?.coleta?.valorColeta || 0)).filter((valor): valor is number => valor !== null));
        }
        return 0;
    }

    function getTipoValorAgregado(): string {
        if (tipoAgregacao === "total") {
            return "Total";
        }
        if (tipoAgregacao === "media") {
            return "Média";
        }
        if (tipoAgregacao === "maximo") {
            return "Máximo";
        }
        if (tipoAgregacao === "minimo") {
            return "Mínimo";
        }
        return "";
    }

    return <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", width: "100%" }}>
        <div style={{ flex: 1 }}>
            <Dropdown
                value={tipoAgregacao}
                optionLabel="label"
                optionValue="value"
                options={[
                    { label: "Total", value: "total" },
                    { label: "Média", value: "media" },
                    { label: "Máximo", value: "maximo" },
                    { label: "Mínimo", value: "minimo" }
                ]}
                onChange={(e) => setTipoAgregacao(e.value)}
                style={{ width: "100%", marginBottom: 12 }}
            />
            <VictoryChart
                theme={VictoryTheme.material}
                domainPadding={40}
                width={400}
                height={700}
                padding={{ top: 0, bottom: 30, left: 80, right: 80 }}
                containerComponent={
                    <VictoryVoronoiContainer
                        voronoiDimension="x"
                    />
                }
            >
                <VictoryAxis
                    tickValues={[getTipoValorAgregado()]}
                    tickFormat={[getTipoValorAgregado()]}
                    style={{
                        tickLabels: { fontSize: 20 },
                        grid: { stroke: '#CCCCCC' }
                    }}
                />
                <VictoryAxis
                    dependentAxis
                    tickFormat={(x) => (`${x}`)}
                    style={{
                        tickLabels: { fontSize: 20 },
                        grid: { stroke: '#CCCCCC' }
                    }}
                />
                <VictoryBar
                    data={[{ x: getTipoValorAgregado(), y: calcularValorAgregado() }]}
                    x={"x"}
                    y={"y"}
                    style={{
                        data: { fill: "#0E9CFF" },
                        labels: { fontSize: 20, fill: "#000", fontWeight: "bold" }
                    }}
                    barWidth={100}
                    labels={({ datum }) => {
                        return `${indicadorService.getValorConvertido(props.unidadeMedida || "", datum.y)}`
                    }}
                />
            </VictoryChart>
        </div>
        <div style={{ width: "80%", height: "85vh" }}>

            <VictoryChart
                theme={VictoryTheme.material}
                domainPadding={60}
                width={800}
                height={400}
                padding={{ top: 0, bottom: 30, left: 60, right: 0 }}
                containerComponent={
                    <VictoryVoronoiContainer
                        voronoiDimension="x"
                    />
                }
            >
                <VictoryAxis
                    tickValues={props.dados.map((dado: IndicadorDashboardItem) => formatarDataColeta(dado.coleta))}
                    tickFormat={props.dados.map((dado: IndicadorDashboardItem) => formatarDataColeta(dado.coleta))}
                    style={{
                        tickLabels: { fontSize: 10 },
                        grid: { stroke: '#CCCCCC' }
                    }}
                />
                <VictoryAxis
                    dependentAxis
                    tickValues={ticks || undefined}
                    tickFormat={(x) => (`${x}`)}
                    style={{
                        tickLabels: { fontSize: 10 },
                        grid: { stroke: '#CCCCCC' }
                    }}
                />
                <VictoryBar
                    data={props.dados.map((dado: IndicadorDashboardItem) => ({
                        x: formatarDataColeta(dado.coleta),
                        y: dado?.coleta?.valorColeta || 0
                    }))}
                    x={"x"}
                    y={"y"}
                    style={{ data: { fill: getColor } }}
                    labels={({ datum }) => {
                        return `${indicadorService.getValorConvertido(props.unidadeMedida || "", datum.y)}`
                    }}
                />
                <VictoryLine
                    data={props.dados.map((dado: IndicadorDashboardItem) => ({
                        x: formatarDataColeta(dado.coleta),
                        y: dado.meta?.meta || 0
                    }))}
                    theme={VictoryTheme.material}
                    y={(datum) => datum.y}
                    style={{ data: { stroke: "#555" } }}
                    interpolation={"linear"}
                    labels={({ datum }: { datum: any }) => {
                        const simbolo = indicadorService.getTipoMeta(props.dados[datum._x - 1].meta?.tipo || "").simbolo;
                        return "Meta:" + simbolo + " " + datum.y
                    }}
                    labelComponent={<VictoryTooltip
                        cornerRadius={10}
                        pointerWidth={30}
                        pointerLength={20}
                        flyoutPadding={{ top: 5, bottom: 5, left: 10, right: 10 }}
                        style={{ fontSize: "16px", fill: "#FFF" }}
                        flyoutStyle={{ fill: "#414B5F" }}
                        orientation="bottom"
                    />}
                />
                <VictoryScatter
                    data={props.dados.map((dado: IndicadorDashboardItem) => ({
                        x: formatarDataColeta(dado.coleta),
                        y: dado.meta?.meta || 0
                    }))}
                    style={{ data: { fill: "#555" } }}
                    y={(datum) => datum.y}

                />

            </VictoryChart>

        </div>
    </div>
}